import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Fetching from '../Fetching';
import ProductListItem from './ProductListItem';
import dataAids from '../../../constants/dataAids';
import { OlsConfigStore, RouteHandler, ShopViewConstants, ScrollWidgetActions } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { CategoryListStore, ShopStore, ProductActions, ProductListStore, CategoryActions } from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import Pagination from '../Pagination';
import ErrorMessage from '../ErrorMessage';
import CategoryList from '../categories/CategoryList';
import SortDropdown from './SortDropdown';
import YotpoCSS from './YotpoCSS';
import { initWidgets } from '../../utils/YotpoUtils';

function hideEmptyStarsContainer() {
  const selector = '.ratings-review-wrapper.product-list .write-review-btn-hidden';
  const noReviewElements = document.querySelectorAll(selector);
  for (const reviewElement of noReviewElements) {
    // NEMO-8659: hide empty stars on Product List page
    reviewElement.parentNode.style.display = 'none';
  }
}

class ProductList extends React.Component {
  constructor() {
    super(...arguments);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.onCategoryStoreChange = this.onCategoryStoreChange.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.state = this.getStoreState();
  }

  getChildContext() {
    const { imageCropMethod, galleryShowBorder, galleryImageShape, showSimilarProducts, pdpImageShape } = this.props;
    return {
      imageCropMethod,
      galleryShowBorder,
      galleryImageShape,
      pdpImageShape,
      showSimilarProducts
    };
  }

  getStoreState() {
    const {
      error,
      errorRetryable,
      products,
      currentPage,
      pages,
      totalCount,
      fetching: fetchingProducts,
      fetched: fetchedProducts
    } = ProductListStore.getState();
    const {
      selectedTaxonPermalink,
      routePath,
      searchKeywords,
      sortOption
    } = ShopStore.getState();
    const {
      taxons,
      fetching: fetchingCategories,
      fetched: fetchedCategories
    } = CategoryListStore.getState();
    const { configWasLoaded } = OlsConfigStore.getState();

    return {
      configWasLoaded,
      fetchedProducts,
      fetchedCategories,
      fetchingProducts,
      fetchingCategories,
      error,
      configError: OlsConfigStore.getState().error,
      errorRetryable,
      products,
      currentPage,
      pages,
      routePath,
      searchKeywords,
      sortOption,
      selectedTaxonPermalink,
      taxons,
      totalCount
    };
  }

  UNSAFE_componentWillMount() {
    ProductListStore.addListener('change', this.onStoreChange);
    CategoryListStore.addListener('change', this.onCategoryStoreChange);
    OlsConfigStore.addListener('change', this.onStoreChange);
  }

  componentWillUnmount() {
    window.removeEventListener('olsProductsUpdated', this.handleProductListReload);
    ProductListStore.removeListener('change', this.onStoreChange);
    CategoryListStore.removeListener('change', this.onCategoryStoreChange);
    OlsConfigStore.removeListener('change', this.onStoreChange);
  }

  componentDidUpdate() {
    initWidgets(hideEmptyStarsContainer, 'hideEmptyStarsListing');
    this.verifyPermalinkParam();
  }

  verifyPermalinkParam = () => {
    const {
      routePath,
      selectedTaxonPermalink,
      fetchingProducts,
      configWasLoaded
    } = this.state;
    const defaultCategory = OlsConfigStore.getDefaultCategory();
    const validRoute = routePath === RouteHandler.PRODUCTS_ROUTE || routePath === null;
    const shouldRefetchProducts = validRoute &&
                                  configWasLoaded &&
                                  defaultCategory &&
                                  !selectedTaxonPermalink &&
                                  !fetchingProducts &&
                                  !this.props.useStaticData;

    if (shouldRefetchProducts) {
      setTimeout(() => {
        ProductActions.loadProducts({ taxonPermalink: defaultCategory });
      }, 0);
    }
  }

  onStoreChange() {
    this.setState(this.getStoreState());
    window.setTimeout(() => this.loadProducts(), 0);
  }

  onCategoryStoreChange() {
    this.setState(this.getStoreState());

    setTimeout(() => {
      this.loadCategories();
    }, 0);
  }

  componentDidMount() {
    window.addEventListener('olsProductsUpdated', this.handleProductListReload);
    setTimeout(() => {
      this.loadProducts();
      this.loadCategories();
    }, 0);
  }

  loadCategories = () => {
    if (this.props.useStaticData) return;
    CategoryActions.loadCategories();
  }

  loadProducts = () => {
    const { configWasLoaded } = OlsConfigStore.getState();
    const { fetched } = ProductListStore.getState();
    if (!configWasLoaded || fetched || this.props.useStaticData) return;
    ProductActions.loadProducts({ taxonPermalink: OlsConfigStore.getDefaultCategory() });
  }

  handleProductListReload = () => {
    ProductActions.loadProducts({ taxonPermalink: OlsConfigStore.getDefaultCategory() });
    this.loadCategories();
  };

  onPageChange(page) {
    const { selectedTaxonPermalink, routePath, searchKeywords, sortOption } = this.state;
    const { currentView } = this.props;
    const data = {
      page: page,
      keywords: searchKeywords,
      routePath,
      taxonPermalink: selectedTaxonPermalink
    };
    if (sortOption) {
      data.sortOption = sortOption;
    }
    RouteHandler.navigate(currentView, data);
    ScrollWidgetActions.scrollShopWidget();
  }

  render() {
    const {
      currentView,
      layoutDesktopColumns,
      layoutMobileColumns,
      defaultSortOption,
      renderMode,
      useStaticData,
      categoryDetails
    } = this.props;
    const translate = this.context.translate || (() => {});
    const { featureFlags: { enableOlsProductSorting = false } } = this.context;
    const {
      products = [],
      error,
      configError,
      currentPage,
      pages,
      taxons,
      selectedTaxonPermalink,
      routePath,
      searchKeywords,
      sortOption,
      totalCount,
      fetchingProducts,
      fetchedProducts,
      fetchingCategories,
      fetchedCategories
    } = this.state;

    if (configError && configError.hasOwnProperty('status') && !useStaticData) {
      return (<ErrorMessage message={ translate('products.error_fetching_product_data') } />);
    }

    const allFetched = fetchedProducts && fetchedCategories;
    if (!allFetched) {
      return (<Fetching fetching={ true } pageTransition={ true } />);
    }

    // The most reliable way of determining whether a product was not found is to check the
    // current olsPage/queryparams. If we are on what looks like a PDP page but see this view, then
    // we can safely assume that the product was not found.
    const showProductNotFound = this.props.showProductNotFound ||
                                /olsPage=products(\/|%2f)[\w]+/i.test(window.location.search) ||
                                /\/ols\/products\/[\w]+/i.test(window.location.pathname);

    const { Block, Text } = UX2.Element;
    const { Grid, Grid: { Cell } } = UX2.Component;
    const { onPageChange } = this;
    let productData;
    const showCategories = taxons.length > 0;
    const isSearchView = currentView === ShopViewConstants.SEARCH_RESULTS;
    const styles = {
      grid: {
        justifyContent:
          (products.length === 1 || products.length === 2) && !showCategories
            ? 'center'
            : 'start'
      },
      noProducts: {
        marginTop: showCategories ? '0' : 'large',
        fontSize: 'large'
      },
      subHeaderWrapper: {
        'marginBottom': 'large',
        'textAlign': 'left',
        '@xs': {
          alignSelf: 'flex-start'
        }
      },
      titleResults: {
        marginLeft: 'small'
      },
      pagination: {
        'display': 'flex',
        'marginTop': 'small',
        'justifyContent': 'center',
        '@sm': {
          justifyContent: 'flex-end'
        }
      },
      showInDesktop: {
        'display': 'none',
        '@sm': {
          display: 'flex'
        }
      },
      showInMobile: {
        'display': 'flex',
        '@sm': {
          display: 'none'
        }
      },
      categoriesCell: {
        '@md': {
          marginTop: 'xxsmall',
          paddingBottom: 'xlarge'
        },
        'paddingBottom': '0 !important',
        'position': 'relative',
        'display': 'flex'
      },
      productNotFound: {
        backgroundColor: 'section',
        padding: 'small',
        marginTop: 'medium',
        marginBottom: 'medium'
      },
      productNotFoundText: {
        fontSize: 'medium',
        textAlign: 'center'
      },
      rowContainer: {
        'display': 'flex',
        'flexDirection': 'row-reverse',
        'justifyContent': 'space-between',
        'alignItems': 'flex-start',
        '@xs-only': {
          flexDirection: 'column',
          alignItems: 'flex-end'
        }
      },
      categoryDesktop: {
        'display': 'flex',
        'flex': 1,
        'marginRight': 'medium',
        'justifyContent': 'flex-start',
        '@xs-only': {
          display: 'none'
        }
      },
      categoryMobile: {
        'justifyContent': 'center',
        '@sm': {
          display: 'none'
        }
      },
      categoryName: {
        'display': 'flex',
        'alignItems': 'flex-start',
        'flexDirection': 'column',
        'marginBottom': 'medium',
        '@xs-only': {
          alignItems: 'center',
          textAlign: 'center'
        }
      }
    };

    if (error && error.hasOwnProperty('status')) {
      productData = (
        <ErrorMessage message={ translate('products.error_fetching_product_data') } />
      );
    } else if (products.length) {
      const productsPerPage = 5 * +layoutDesktopColumns;

      productData = (
        <Grid
          data-aid={ dataAids.PRODUCT_LIST_RENDERED }
          data-products-per-page={ productsPerPage }
          xs={ +layoutMobileColumns }
          md={ +layoutDesktopColumns }
          style={ styles.grid }
        >
          {products.map(product => (
            <Cell key={ product.slug } style={{ marginBottom: 'large' }}>
              <ProductListItem
                product={ product }
                renderMode={ renderMode }
                cardSize={{
                  lg: (+layoutDesktopColumns > 3 && showCategories) ? 'small' : 'large',
                  md: (+layoutDesktopColumns > 3 || (+layoutDesktopColumns > 2 && showCategories)) ? 'small' : 'large',
                  xs: +layoutMobileColumns > 1 ? 'small' : 'large'
                }}
              />
            </Cell>
          ))}
        </Grid>
      );
    } else if (!isSearchView) {
      productData = (
        <Grid size={ 6 }>
          <Cell
            xs={ 6 }
            sm={ 4 }
            pushSm={ showCategories ? 0 : 1 }
            style={{ textAlign: 'center' }}
          >
            <Text
              data-aid={ dataAids.NO_PRODUCTS_MESSAGE_RENDERED }
              style={ styles.noProducts }
            >
              <Block
                tag='span'
                children={ translate('products.no_products_message') }
              />
            </Text>
          </Cell>
        </Grid>
      );
    }

    let resultsString;
    if (isSearchView) {
      if (totalCount === 0) {
        resultsString = 'no_search_results';
      } else if (totalCount === 1) {
        resultsString = 'search_results_page_singular';
      } else {
        resultsString = 'search_results_page_plural';
      }
    }

    let resultTitleString = 'no_search_results';
    if (isSearchView && totalCount > 0) {
      resultTitleString = 'search_results_page_title';
    }

    const title = translate(`products.${resultTitleString}`, {
      query: searchKeywords
    });

    const resultCount = translate(`products.${resultsString}`, {
      count: totalCount
    });

    const resultsCountDetail = (renderClass = null) => {
      let resultStyle = {
        whiteSpace: 'nowrap'
      };
      if (!renderClass) {
        resultStyle = {
          ...resultStyle,
          marginLeft: 0
        };
      }

      return totalCount > 0 &&
        (<UX2.Element.Details
          tag='span'
          disabled={ true }
          children={ resultCount }
          style={{ ...renderClass, ...resultStyle }}
        />);
    };

    const searchTitle = isSearchView && (
      <UX2.Group.Group style={ styles.showInDesktop }>
        <UX2.Element.Heading.Product
          data-aid={ totalCount > 0 ? dataAids.SHOP_TITLE_RENDERED : dataAids.SEARCH_NO_RESULTS_MESSAGE_RENDERED }
        >
          { title }
          { resultsCountDetail(styles.titleResults) }
        </UX2.Element.Heading.Product>
      </UX2.Group.Group>
    );

    const categorySelected = showCategories && !categoryDetails.image && categoryDetails.name && (
      <UX2.Group.Group>
        <UX2.Element.Heading.Product
          style={ styles.categoryName }
          data-aid={ dataAids.SHOP_CATEGORIES }
        >
          { categoryDetails.name }
          { categoryDetails.description && <Text children={ categoryDetails.description }/> }
        </UX2.Element.Heading.Product>
      </UX2.Group.Group>
    );

    const productsContainer = (
      <Block>
        <YotpoCSS products={ products } />
        <Block style={ styles.rowContainer }>
          {enableOlsProductSorting && (
            <SortDropdown
              renderMode={ renderMode }
              translate={ translate }
              currentView={ currentView }
              currentSortOption={ sortOption || defaultSortOption }
              products={ products }
              fetching={ !allFetched }
              currentPage={ currentPage }
              selectedTaxonPermalink={ selectedTaxonPermalink }
              routePath={ routePath }
              searchKeywords={ searchKeywords }
              totalCount={ totalCount }
            />
          )}
          <Block style={ styles.categoryDesktop }>
            { categorySelected }
          </Block>
          {isSearchView && (
            <Block style={ styles.subHeaderWrapper }>
              { searchTitle }
              <Block style={ styles.showInMobile }>{ resultsCountDetail() } </Block>
            </Block>
          )}
        </Block>
        {showProductNotFound && renderMode === 'PREVIEW' && (
          <Block
            section='alt'
            data-aid={ dataAids.PRODUCT_NOT_FOUND_MESSAGE_RENDERED }
            style={ styles.productNotFound }
          >
            <Text
              style={ styles.productNotFoundText }
              children={ translate('products.product_no_longer_available_2') }
            />
          </Block>
        )}
        {productData}
        {pages > 1 && (
          <Block style={ styles.pagination }>
            <Pagination
              currentPage={ currentPage }
              pages={ pages }
              onPageChange={ onPageChange }
            />
          </Block>
        )}
      </Block>
    );

    return (
      <Block style={{ position: 'relative' }} data-route='' >
        <Block style={{ position: 'relative' }}>
          <Block style={ styles.categoryMobile }>
            { categorySelected }
          </Block>
          { showCategories ? (
            <Grid xs={ 1 } sm={ 4 }>
              <Cell size={ 1 } style={ styles.categoriesCell }>
                <CategoryList
                  renderMode={ renderMode }
                  taxons={ taxons }
                  selectedTaxonPermalink={ selectedTaxonPermalink }
                  routePath={ routePath }
                  isSearch={ isSearchView }
                  currentSortOption={ sortOption }
                />
              </Cell>
              <Cell xs={ 1 } sm={ 3 } children={ productsContainer } />
            </Grid>
          ) : productsContainer
          }
        </Block>
        <Fetching
          fetching={ fetchingProducts || fetchingCategories }
          pageTransition={ true }
        />
      </Block>
    );
  }
}

ProductList.propTypes = {
  imageCropMethod: PropTypes.string,
  currentView: PropTypes.any,
  layoutDesktopColumns: PropTypes.number,
  layoutMobileColumns: PropTypes.number,
  defaultSortOption: PropTypes.string,
  renderMode: PropTypes.string,
  products: PropTypes.object,
  useStaticData: PropTypes.bool,
  galleryShowBorder: PropTypes.bool,
  galleryImageShape: PropTypes.string,
  pdpImageShape: PropTypes.string,
  showSimilarProducts: PropTypes.bool,
  // Used primarily for testing:
  showProductNotFound: PropTypes.bool,
  categoryDetails: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string
  }).isRequired
};

ProductList.contextTypes = {
  translate: PropTypes.func,
  featureFlags: PropTypes.object
};

ProductList.childContextTypes = {
  imageCropMethod: PropTypes.string,
  galleryShowBorder: PropTypes.bool,
  galleryImageShape: PropTypes.string,
  pdpImageShape: PropTypes.string,
  showSimilarProducts: PropTypes.bool
};

export default ProductList;
